import React from 'react';
import { VideoCornerColor } from '../../types/video.types';

export type FighterCornerLayout = 'row' | 'stack';
export type FighterCornerSize = 'sm' | 'md';

export interface FighterCornerBarsProps {
    leftName: string;
    leftColor?: VideoCornerColor | null;
    rightName: string;
    rightColor?: VideoCornerColor | null;
    layout?: FighterCornerLayout;
    size?: FighterCornerSize;
    stackOnMobile?: boolean;
    className?: string;
}

interface FighterCornerBarProps {
    name: string;
    cornerColor?: VideoCornerColor | null;
    size: FighterCornerSize;
}

const getCornerStyles = (cornerColor?: VideoCornerColor | null) => {
    if (cornerColor === 'blue') {
        return {
            bar: 'bg-defaultBlue text-white',
            accent: 'bg-white/30'
        };
    }

    if (cornerColor === 'red') {
        return {
            bar: 'bg-defaultRed text-white',
            accent: 'bg-white/30'
        };
    }

    return {
        bar: 'bg-slate-200 text-slate-700',
        accent: 'bg-white/70'
    };
};

/**
 * Corner bars for fighter matchups (name-only for now, extensible later).
 */
const FighterCornerBars = ({
    leftName,
    leftColor,
    rightName,
    rightColor,
    layout = 'row',
    size = 'md',
    stackOnMobile = false,
    className
}: FighterCornerBarsProps) => {
    const layoutClasses = stackOnMobile
        ? 'flex flex-col items-stretch gap-1 sm:flex-row sm:items-center sm:gap-2'
        : layout === 'stack'
        ? 'flex flex-col items-stretch gap-1'
        : 'flex items-center gap-2';
    const vsClasses = size === 'sm'
        ? 'text-[10px] tracking-[0.2em]'
        : 'text-[11px] tracking-[0.25em]';
    const vsPositionClasses = stackOnMobile
        ? 'self-center sm:self-auto'
        : layout === 'stack'
            ? 'self-center'
            : '';

    return (
        <div className={`${layoutClasses} ${className ?? ''}`}>
            <FighterCornerBar name={leftName} cornerColor={leftColor ?? null} size={size} />
            <span className={`font-semibold text-slate-400 ${vsClasses} ${vsPositionClasses}`}>V</span>
            <FighterCornerBar name={rightName} cornerColor={rightColor ?? null} size={size} />
        </div>
    );
};

const FighterCornerBar = ({ name, cornerColor, size }: FighterCornerBarProps) => {
    const styles = getCornerStyles(cornerColor);
    const sizeClasses = size === 'sm'
        ? 'px-2.5 py-1.5 text-xs'
        : 'px-3 py-2 text-sm';

    return (
        <div className="min-w-0">
            <div className={`relative flex min-w-0 items-center overflow-hidden rounded-md ${sizeClasses} ${styles.bar} shadow-md ring-1 ring-black/10`}>
                <span className={`absolute left-0 top-0 h-full w-1 ${styles.accent}`} aria-hidden="true" />
                <span className="relative truncate font-semibold leading-tight">{name}</span>
            </div>
        </div>
    );
};

export default FighterCornerBars;
